/**
 * UnifiedResolutionDrawer
 * ────────────────────────
 * Right-side slide-in shell that aggregates contextual triage data
 * for a single job: customer/address/installer summary, the originating
 * event (when present), related execution context (callbacks, holdbacks,
 * issues, materials, QA, readiness), and a small set of deep-link
 * actions to existing routes.
 *
 * Hard rules (locked by spec):
 *  - Read-only: no writers, no mutations, no automation, no notifications.
 *  - No financial fields, no PII beyond name + city/address.
 *  - Reuses canonical sources only.
 *  - Single instance per provider mount; opens via the resolutionDrawerBus.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAppData } from "@/data/AppDataContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { toast } from "@/hooks/use-toast";
import {
  registerResolutionDrawerOpener,
  type OpenResolutionDrawerOptions,
  type ResolutionDrawerSeverity,
  type ResolutionDrawerSource,
} from "@/data/execution/resolutionDrawerBus";
import {
  buildResolutionContext,
  type ResolutionContext,
} from "@/data/execution/resolutionContext";
import { formatRelativeTime } from "@/data/execution/eventStream";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Copy,
  Layers,
  MapPin,
  PauseCircle,
  ShieldCheck,
  Sparkles,
  User,
  Wrench,
  XCircle,
} from "lucide-react";

export const SOURCE_LABEL: Record<ResolutionDrawerSource, string> = {
  live_stream: "Live Stream",
  signal_cluster: "Signal Cluster",
  install_today_blocked: "Install Today",
  qa_callback_row: "QA Callback",
  holdback_row: "Holdback",
};

export const SOURCE_TITLE: Record<ResolutionDrawerSource, string> = {
  live_stream: "Live Event Triage",
  signal_cluster: "Cluster Triage",
  install_today_blocked: "Install Blocker Triage",
  qa_callback_row: "Callback Triage",
  holdback_row: "Holdback Triage",
};

export const SOURCE_BREADCRUMB: Record<ResolutionDrawerSource, string> = {
  live_stream: "Execution · Live Stream",
  signal_cluster: "Execution · Signal Clusters",
  install_today_blocked: "Execution · Install Today",
  qa_callback_row: "Execution · QA & Callbacks",
  holdback_row: "Execution · Holdbacks",
};

/**
 * Pure tint resolver. Explicit event severity wins; otherwise derive
 * from related counts. Critical = active holdback or QA failed.
 */
export function resolveDrawerSeverity(
  ctx: ResolutionContext,
  explicit?: ResolutionDrawerSeverity,
): ResolutionDrawerSeverity {
  if (explicit) return explicit;
  if (!ctx.jobFound) return "info";
  if (
    ctx.related.activeHoldbackCount > 0 ||
    ctx.related.qaStatus === "failed"
  ) {
    return "critical";
  }
  if (
    ctx.related.openIssueCount > 0 ||
    ctx.related.callbackCount > 0 ||
    ctx.related.openTicketCount > 0 ||
    ctx.related.unresolvedFieldFeedbackCount > 0 ||
    ctx.related.openSubstitutionCount > 0
  ) {
    return "warn";
  }
  return "info";
}

const SEVERITY_HEADER_CLS: Record<ResolutionDrawerSeverity, string> = {
  info: "bg-muted/30",
  warn: "bg-warning/10 border-warning/40",
  critical: "bg-destructive/10 border-destructive/40",
};

const SEVERITY_BADGE_CLS: Record<ResolutionDrawerSeverity, string> = {
  info: "border-border text-muted-foreground",
  warn: "border-warning/40 text-warning",
  critical: "border-destructive/40 text-destructive",
};

export function hasAnyRelatedContext(ctx: ResolutionContext): boolean {
  if (!ctx.jobFound) return false;
  const r = ctx.related;
  return (
    r.callbackCount > 0 ||
    r.activeHoldbackCount > 0 ||
    r.openIssueCount > 0 ||
    r.openTicketCount > 0 ||
    r.unresolvedFieldFeedbackCount > 0 ||
    r.openSubstitutionCount > 0 ||
    r.qaStatus === "failed" ||
    r.qaStatus === "awaiting"
  );
}

function buildCopyPayload(ctx: ResolutionContext): string {
   const lines = [
     `Job: ${ctx.jobNumber}`,
     `Customer: ${ctx.customerName}`,
     `Address: ${ctx.address}`,
     `Installer: ${ctx.installerName}`,
     `Phase: ${ctx.currentPhase}`,
     `Lifecycle: ${ctx.lifecycle}`,
   ];
   return lines.join("\n");
 }

export function UnifiedResolutionDrawer() {
  const data = useAppData();
  const { fieldMaterialFeedback, substitutionRequests } = useVendorPO();
  const [opts, setOpts] = useState<OpenResolutionDrawerOptions | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const unregister = registerResolutionDrawerOpener((o) => {
      setOpts(o);
      setNow(Date.now());
    });
    return unregister;
  }, []);

  const ctx: ResolutionContext | null = useMemo(() => {
    if (!opts) return null;
    return buildResolutionContext({
      jobId: opts.jobId,
      source: opts.source,
      eventContext: opts.eventContext,
      jobs: data.jobs,
      contractors: data.contractors,
      callbacks: data.callbacks,
      holdbackLedger: data.holdbackLedger,
      issues: data.issues,
      serviceTickets: data.serviceTickets,
      fieldMaterialFeedback: fieldMaterialFeedback ?? [],
      substitutionRequests: substitutionRequests ?? [],
      specExecutions: data.specExecutions,
    });
  }, [
    opts,
    data.jobs,
    data.contractors,
    data.issues,
    data.serviceTickets,
    data.specExecutions,
    fieldMaterialFeedback,
    substitutionRequests,
  ]);

  const severity: ResolutionDrawerSeverity = useMemo(
    () =>
      ctx ? resolveDrawerSeverity(ctx, opts?.eventContext?.severity) : "info",
    [ctx, opts?.eventContext?.severity],
  );

  const open = opts !== null;
  const handleOpenChange = (next: boolean) => {
    if (!next) setOpts(null);
  };

  const handleCopy = async () => {
    if (!ctx) return;
    const payload = buildCopyPayload(ctx);
    try {
      await navigator.clipboard?.writeText(payload);
      toast({ title: "Copied job details", description: ctx.customerName });
    } catch {
      toast({
        title: "Copy failed",
        description: "Clipboard unavailable.",
        variant: "destructive",
      });
    }
  };

  const headerCls = SEVERITY_HEADER_CLS[severity];
  const sourceTitle = opts ? SOURCE_TITLE[opts.source] : "Resolution Drawer";
  const breadcrumb = opts ? SOURCE_BREADCRUMB[opts.source] : "";

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        data-testid="unified-resolution-drawer"
        data-severity={severity}
        className="w-full sm:max-w-md p-0 flex flex-col overflow-hidden"
      >
        <SheetHeader
          className={`px-5 py-3 border-b ${headerCls}`}
          data-testid="resolution-drawer-header"
        >
          <div className="flex items-center justify-between gap-2">
            <SheetTitle
              className="text-sm flex items-center gap-2"
              data-testid="resolution-drawer-title"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {sourceTitle}
            </SheetTitle>
            <Badge
              variant="outline"
              className={`text-[10px] ${SEVERITY_BADGE_CLS[severity]} capitalize`}
              data-testid="resolution-drawer-severity"
            >
              {severity}
            </Badge>
          </div>
          <SheetDescription
            className="text-[11px] flex items-center gap-1 flex-wrap"
            data-testid="resolution-drawer-breadcrumb"
          >
            <span>{breadcrumb}</span>
            {ctx?.jobFound && (
              <>
                <ChevronRight className="h-3 w-3 opacity-50" />
                <span>{ctx.jobNumber}</span>
              </>
            )}
          </SheetDescription>
        </SheetHeader>

        {ctx && (
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 text-[12px]">
            {!ctx.jobFound ? (
              <p
                className="text-muted-foreground"
                data-testid="resolution-job-missing"
              >
                Job not found. It may have been archived or removed.
              </p>
            ) : (
              <>
                <Section icon={User} title="Job">
                  <Field label="Customer" value={ctx.customerName} />
                  <Field
                    label="Address"
                    value={ctx.address}
                    icon={MapPin}
                  />
                  <Field label="Installer" value={ctx.installerName} />
                  <Field label="Current phase" value={ctx.currentPhase} />
                  <Field label="Lifecycle" value={ctx.lifecycle} />
                  <div className="pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] gap-1.5"
                      onClick={handleCopy}
                      data-testid="resolution-copy-details"
                    >
                      <Copy className="h-3 w-3" />
                      Copy job & customer details
                    </Button>
                  </div>
                </Section>

                {opts?.eventContext && (
                  <Section icon={Layers} title="Event">
                    <Field
                      label="Type"
                      value={opts.eventContext.kind.replace(/_/g, " ")}
                    />
                    {opts.eventContext.headline && (
                      <Field
                        label="Headline"
                        value={opts.eventContext.headline}
                      />
                    )}
                    {opts.eventContext.detail && (
                      <Field
                        label="Detail"
                        value={opts.eventContext.detail}
                      />
                    )}
                    {opts.eventContext.at && (
                      <Field
                        label="When"
                        value={formatRelativeTime(opts.eventContext.at, now)}
                      />
                    )}
                    <Field
                      label="Source"
                      value={SOURCE_LABEL[opts.source]}
                    />
                  </Section>
                )}

                <Section icon={ClipboardList} title="Related context">
                  {!hasAnyRelatedContext(ctx) ? (
                    <p
                      className="text-[11px] text-muted-foreground italic"
                      data-testid="resolution-related-empty"
                    >
                      No related callbacks, holdbacks, issues, or open work
                      against this job.
                    </p>
                  ) : (
                    <>
                      <RelatedRow
                        icon={QaIcon(ctx.related.qaStatus)}
                        label="QA"
                        value={ctx.related.qaStatus.replace(/_/g, " ")}
                        tone={
                          ctx.related.qaStatus === "failed"
                            ? "bad"
                            : ctx.related.qaStatus === "awaiting"
                              ? "warn"
                              : undefined
                        }
                      />
                      <RelatedRow
                        icon={AlertTriangle}
                        label="Callbacks"
                        value={String(ctx.related.callbackCount)}
                        tone={
                          ctx.related.callbackCount > 0 ? "warn" : undefined
                        }
                      />
                      <RelatedRow
                        icon={PauseCircle}
                        label="Active holdbacks"
                        value={String(ctx.related.activeHoldbackCount)}
                        tone={
                          ctx.related.activeHoldbackCount > 0
                            ? "bad"
                            : undefined
                        }
                      />
                      <RelatedRow
                        icon={AlertTriangle}
                        label="Open issues"
                        value={String(ctx.related.openIssueCount)}
                        tone={
                          ctx.related.openIssueCount > 0 ? "warn" : undefined
                        }
                      />
                      <RelatedRow
                        icon={Wrench}
                        label="Open tickets"
                        value={String(ctx.related.openTicketCount)}
                        tone={
                          ctx.related.openTicketCount > 0 ? "warn" : undefined
                        }
                      />
                      <RelatedRow
                        icon={Wrench}
                        label="Field material issues"
                        value={String(ctx.related.unresolvedFieldFeedbackCount)}
                        tone={
                          ctx.related.unresolvedFieldFeedbackCount > 0
                            ? "warn"
                            : undefined
                        }
                      />
                      <RelatedRow
                        icon={Layers}
                        label="Open substitutions"
                        value={String(ctx.related.openSubstitutionCount)}
                        tone={
                          ctx.related.openSubstitutionCount > 0
                            ? "warn"
                            : undefined
                        }
                      />
                      <RelatedRow
                        icon={ShieldCheck}
                        label="Readiness"
                        value={
                          ctx.related.readinessLocked ? "Locked" : "Not locked"
                        }
                      />
                      <RelatedRow
                        icon={ShieldCheck}
                        label="Permit"
                        value={ctx.related.permitStatus}
                      />
                      <RelatedRow
                        icon={ClipboardList}
                        label="Materials"
                        value={ctx.related.materialReadiness}
                      />
                      <RelatedRow
                        icon={ClipboardList}
                        label="Dispatch"
                        value={ctx.related.dispatchStatus}
                      />
                    </>
                  )}
                </Section>

                <Section icon={Sparkles} title="Open in">
                  <div
                    className="flex flex-wrap gap-1.5"
                    data-testid="resolution-deep-links"
                  >
                    {ctx.deepLinks.map((d) => (
                      <Link
                        key={d.id}
                        to={d.href}
                        title={d.hint}
                        data-testid={`resolution-link-${d.id}`}
                        onClick={() => setOpts(null)}
                        className="inline-flex items-center px-2 py-1 rounded-md text-[11px] font-medium border bg-muted/40 text-foreground border-border hover:bg-muted/70 transition-colors"
                      >
                        {d.label}
                      </Link>
                    ))}
                  </div>
                </Section>

                <p className="text-[10px] text-muted-foreground italic">
                  Read-only triage shell. No actions are taken from this
                  drawer — use the deep-links to act in canonical surfaces.
                </p>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function QaIcon(status: ResolutionContext["related"]["qaStatus"]) {
  if (status === "passed") return CheckCircle2;
  if (status === "failed") return XCircle;
  if (status === "awaiting") return ShieldCheck;
  return ShieldCheck;
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
        <Icon className="h-3 w-3" />
        <span>{title}</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="w-24 shrink-0 text-[10.5px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="flex-1 text-foreground flex items-center gap-1 min-w-0">
        {Icon && <Icon className="h-3 w-3 text-muted-foreground" />}
        <span className="truncate">{value}</span>
      </span>
    </div>
  );
}

function RelatedRow({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone?: "warn" | "bad";
}) {
  const toneCls =
    tone === "bad"
      ? "border-destructive/40 text-destructive"
      : tone === "warn"
        ? "border-warning/40 text-warning"
        : "border-border text-muted-foreground";
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-foreground">
        <Icon className="h-3 w-3 text-muted-foreground" />
        {label}
      </span>
      <Badge
        variant="outline"
        className={`text-[10px] ${toneCls} capitalize`}
      >
        {value}
      </Badge>
    </div>
  );
}

export default UnifiedResolutionDrawer;
